import { computed, inject, Injectable, resource } from '@angular/core';
import { equals, isDefined } from '@softline/core';
import { ComponentSettings, PageSettings } from './page-settings';
import { PageSettingsRepository } from './page-settings.repository';
import { AuthenticationContextService } from '@softline/auth';

@Injectable({providedIn: 'root'})
export class PageSettingsService {

  private authService = inject(AuthenticationContextService);

  private readonly repository = inject(PageSettingsRepository);
  private readonly _resource = resource({
    defaultValue: [],
    request: () => ({
      userId: this.authService.userId()
    }),
    loader: async (request) => {
      if(!isDefined(request.request.userId))
        return [];
      return await this.repository.load({ userId: request.request.userId });
    }
  })

  pageSettings = (name: string) => computed<PageSettings>(() => {
    const settings = this._resource.value();
    return (settings ?? []).find(
      o => o.name === name
    ) ?? {name: name, components: [] as ComponentSettings[]}
  });

  async set(page: string, componentSettings: ComponentSettings): Promise<void> {
    const userId = this.authService.userId();
    if(!isDefined(userId))
      throw new Error('[PageSettingsService] set: userId is not defined');
    const pageSettings = this.pageSettings(page)();
    const index = pageSettings.components.findIndex(o => o.name === componentSettings.name);
    if(index > -1 && equals(componentSettings, pageSettings.components[index]))
      return;
    const components = [...pageSettings.components];
    if(index > -1)
      components.splice(index, 1, componentSettings);
    else
      components.push(componentSettings);

    const loadedPageSettings = await this.loadPageSetting(userId, page);

    let result: PageSettings;

    if (!loadedPageSettings)
      result = await this.repository.create({...pageSettings, components}, {userId});
    else
      result = await this.repository.update(pageSettings.name, {...pageSettings, components}, {userId});

    this.updatePageSettings(result);
  }

  private async loadPageSetting(userId: number, page: string): Promise<PageSettings | undefined> {
    const settings = await this.repository.load({ userId });
    return (settings ?? []).find(
      o => o.name === page
    )
  }

  private updatePageSettings(pageSettings: PageSettings): void {
    const value = this._resource.value();
    if (!value) {
      this._resource.set([pageSettings]);
      return;
    }
    const index = value.findIndex(o => o.name === pageSettings.name);
    if (index > -1) {
      value[index] = pageSettings;
      this._resource.set(value);
    } else {
      this._resource.set([...value, pageSettings]);
    }
  }
}
